import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { Link as RouterLink } from "react-router-dom";
import { ROUTES } from "../../../shared/utils";
import { AuthCardIntro } from "./AuthCardHeader";
import { useForgotPassword } from "../mutations";
import { ResetPasswordCodeVerification } from "./ResetPasswordCodeVerification";

type ResetStep = "email" | "verify" | "success";

interface ResetPasswordFlowProps {
  onBackToLogin: () => void;
}

export const ResetPasswordFlow: React.FC<ResetPasswordFlowProps> = ({
  onBackToLogin,
}) => {
  const [step, setStep] = useState<ResetStep>("email");
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState("");

  const forgotPasswordMutation = useForgotPassword();

  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);

  const isEmailValid = emailRegex.test(email);
  const showEmailError = touched && !isEmailValid;

  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    setError("");

    if (!isEmailValid) {
      return;
    }

    try {
      await forgotPasswordMutation.mutateAsync({
        username: email.trim(),
      });
      setStep("verify");
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to send reset code"
      );
    }
  };

  const handleCodeVerificationSuccess = () => {
    setStep("success");
  };

  const handleCodeVerificationError = (error: string) => {
    setError(error);
  };

  const handleBackToEmail = () => {
    setStep("email");
    setError("");
  };

  if (step === "verify") {
    return (
      <ResetPasswordCodeVerification
        username={email}
        onSuccess={handleCodeVerificationSuccess}
        onError={handleCodeVerificationError}
        onBack={handleBackToEmail}
      />
    );
  }

  if (step === "success") {
    return (
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: 4,
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <CheckCircleOutlineRoundedIcon
              color="success"
              sx={{ fontSize: 64 }}
            />
            <Typography variant="h5" component="h1" fontWeight={600}>
              Password reset complete
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You can now sign in with your new password. Keep it safe!
            </Typography>
          </Stack>
          <Button
            size="large"
            variant="contained"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={onBackToLogin}
            sx={{ mt: 2 }}
          >
            Return to Sign In
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleEmailSubmit}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
        }}
      >
        <Stack spacing={4}>
          <AuthCardIntro
            title="Forgot your password?"
            subtitle="Enter the email linked to your account and we'll send a reset code."
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Email address"
              value={email}
              type="email"
              onChange={(event) => {
                setEmail(event.target.value);
                setTouched(true);
              }}
              onBlur={() => setTouched(true)}
              error={showEmailError}
              placeholder="Enter your email address"
              helperText={
                showEmailError ? "Please enter a valid email address." : ""
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <Stack spacing={2}>
            <Button
              type="submit"
              size="large"
              variant="contained"
              fullWidth
              endIcon={<ArrowForwardRoundedIcon />}
              disabled={!email.length || forgotPasswordMutation.isPending}
              sx={{ mt: 2 }}
            >
              {forgotPasswordMutation.isPending
                ? "Sending code..."
                : "Send reset code"}
            </Button>
            <Link
              component={RouterLink}
              to={ROUTES.LOGIN}
              variant="body2"
              underline="hover"
              sx={{ alignSelf: "center", fontWeight: 500 }}
            >
              Remembered your password? Sign in
            </Link>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
